/**
 * 论证弧(narrativeRoles)展示层 —— 导演交代卡「论证弧 pills」+ 时间轴「论证弧色带」共用
 * (W4 批,2026-09-13,设计稿拍板)。
 *
 * ★与 `narrativeRoles.ts` 的 `NARRATIVE_ROLE_LABELS` 是两套措辞,故意不合并:
 * 那一套是矮卡/结构轨的既有措辞(开场钩子/蓄势/收束),已经在跑、有历史判据钉着;
 * 这一套是本批设计稿钉死的新措辞(钩子/铺垫/转折/收束展开/行动号召),只在本批新增的
 * 两处(交代卡 pills / 时间轴色带)用。枚举 key 逐字对齐 ai 侧导演契约,与
 * `NARRATIVE_ROLE_LABELS` 同源(见该文件头注),只是翻译不同。
 */

/** 论证弧 pill / 色带的中文措辞(设计稿钉死,与 narrativeRoles.ts 的措辞不是同一套)。 */
export const ARGUMENT_ARC_LABELS: Record<string, string> = {
  hook: "钩子",
  build_tension: "铺垫",
  turn: "转折",
  resolution: "收束展开",
  cta: "行动号召",
};

/**
 * 论证弧色带/pill 的浅底深字配色(与既有段彩条 / 节拍条视觉区分,克制不抢戏)。
 * 未知角色不给色 —— 调用方按缺色退回中性样式,不硬造。
 */
export const ARGUMENT_ARC_TINT: Record<string, string> = {
  hook: "border-indigo-300/50 bg-indigo-50 text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-950/30 dark:text-indigo-300",
  build_tension:
    "border-amber-300/50 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-300",
  turn: "border-blue-300/50 bg-blue-50 text-blue-700 dark:border-blue-400/30 dark:bg-blue-950/30 dark:text-blue-300",
  resolution:
    "border-emerald-300/50 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-300",
  cta: "border-purple-300/50 bg-purple-50 text-purple-700 dark:border-purple-400/30 dark:bg-purple-950/30 dark:text-purple-300",
};

export interface ArgumentArcRun {
  role: string;
  label: string;
  count: number;
}

/**
 * narrativeRoles 逐镜数组 →「连续同 role 聚合成段,计数」(交代卡 pills 用)。
 * 空/未知角色跳过(宁可这一镜不进论证弧,也不给错的标签 —— 与 `narrativeRoleLabel` 同纪律)。
 */
export function argumentArcRuns(roles?: (string | null | undefined)[] | null): ArgumentArcRun[] {
  if (!roles || roles.length === 0) return [];
  const runs: ArgumentArcRun[] = [];
  for (const r of roles) {
    const key = (r ?? "").trim();
    const label = ARGUMENT_ARC_LABELS[key];
    if (!label) continue;
    const last = runs[runs.length - 1];
    if (last && last.role === key) last.count += 1;
    else runs.push({ role: key, label, count: 1 });
  }
  return runs;
}

/** 「钩子 ×2 → 铺垫 ×3 → …」纯文本形式(测试断言 / 极简展示用)。 */
export function formatArgumentArc(runs: ArgumentArcRun[]): string {
  return runs.map((r) => `${r.label} ×${r.count}`).join(" → ");
}

export interface ArgumentArcSecRun {
  role: string;
  label: string;
  sec: number;
}

/**
 * narrativeRoles + 对应镜时长 →「连续同 role 聚合成段,按秒累加」(时间轴论证弧色带用,
 * 宽度按秒等比,与 VideoTimelineBand 现有段条/节拍条同口径)。
 * `durations[i]` 缺省按 0 计(不占宽度但不报错)。
 */
export function argumentArcSecRuns(
  roles: (string | null | undefined)[] | null | undefined,
  durations: (number | null | undefined)[]
): ArgumentArcSecRun[] {
  if (!roles || roles.length === 0) return [];
  const runs: ArgumentArcSecRun[] = [];
  roles.forEach((r, i) => {
    const key = (r ?? "").trim();
    const label = ARGUMENT_ARC_LABELS[key];
    if (!label) return;
    const sec = durations[i] || 0;
    const last = runs[runs.length - 1];
    if (last && last.role === key) last.sec += sec;
    else runs.push({ role: key, label, sec });
  });
  return runs;
}
